import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as QRCode from 'qrcode';

const CANVAS_WIDTH = 900;
const CANVAS_HEIGHT = 1200;

/**
 * Plantilla de invitación editable para imprimir en un mural o compartir en
 * redes sociales -- incorpora el mismo QR que "Descargar solo QR" (apunta a
 * la pantalla de acceso simple, nunca con el código embebido), pero permite
 * al propietario personalizar colores y textos antes de descargarla, como
 * una tarjeta/invitación real. Se dibuja en un <canvas> y se exporta a PNG.
 */
@Component({
  selector: 'app-invitation-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="ie-overlay" (click)="close.emit()">
      <div class="ie-modal" (click)="$event.stopPropagation()">
        <header>
          <h3>Editar imagen de acceso</h3>
          <button type="button" class="ie-close" (click)="close.emit()" aria-label="Cerrar">×</button>
        </header>
        <div class="ie-body">
          <div class="ie-preview">
            <canvas #canvas [width]="canvasWidth" [height]="canvasHeight"></canvas>
          </div>
          <div class="ie-controls">
            <label class="ie-field" *ngIf="spaces.length">
              <span>Aplica a</span>
              <select [(ngModel)]="selectedScope" (ngModelChange)="onScopeChange()">
                <option value="business">Todo el negocio</option>
                <option *ngFor="let space of spaces" [ngValue]="space.id">{{ space.title }}</option>
              </select>
              <small class="ie-scope-hint">El código de acceso es el mismo para todo el negocio; solo cambia a qué enlace lleva la imagen.</small>
            </label>
            <label class="ie-field">
              <span>Título</span>
              <input placeholder="Ej: ¡Te esperamos en nuestro espacio!" type="text" [(ngModel)]="title" (ngModelChange)="redraw()" maxlength="60">
            </label>
            <label class="ie-field">
              <span>Mensaje</span>
              <textarea placeholder="Ej: Reserva en línea en segundos y recibe tu confirmación al instante." rows="3" [(ngModel)]="subtitle" (ngModelChange)="redraw()" maxlength="160"></textarea>
            </label>
            <div class="ie-field-row">
              <label class="ie-field">
                <span>Color de fondo</span>
                <input type="color" [(ngModel)]="backgroundColor" (ngModelChange)="redraw()">
              </label>
              <label class="ie-field">
                <span>Color de acento</span>
                <input type="color" [(ngModel)]="accentColor" (ngModelChange)="redraw()">
              </label>
              <label class="ie-field">
                <span>Color de texto</span>
                <input type="color" [(ngModel)]="textColor" (ngModelChange)="redraw()">
              </label>
            </div>
            <label class="ie-field">
              <span>Mostrar código como texto</span>
              <div class="ie-toggle-row">
                <label class="ie-switch">
                  <input type="checkbox" [(ngModel)]="showCode" (ngModelChange)="redraw()">
                  <span class="ie-switch-track"><span class="ie-switch-knob"></span></span>
                </label>
                <small>{{ showCode ? 'El código se ve impreso en la imagen' : 'Solo el QR, sin el código visible' }}</small>
              </div>
            </label>
            <div class="ie-actions">
              <button type="button" class="ie-btn ie-btn-ghost" (click)="close.emit()">Cancelar</button>
              <button type="button" class="ie-btn ie-btn-primary" (click)="download()">Descargar imagen</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host{display:block}
    .ie-overlay{position:fixed;inset:0;z-index:1000;display:grid;place-items:center;padding:1rem;background:var(--ts-bg-navy-950-a72);backdrop-filter:blur(4px)}
    .ie-modal{width:min(880px,100%);max-height:calc(100dvh - 2rem);overflow:auto;border-radius:18px;background:var(--ts-bg-navy-850-30);border:1px solid var(--ts-border-navy-700-3);box-shadow:0 30px 70px var(--ts-shadow-gray-1000-a45)}
    .ie-modal header{display:flex;align-items:center;justify-content:space-between;padding:1rem 1.25rem;border-bottom:1px solid var(--ts-border-navy-700-3)}
    .ie-modal header h3{margin:0;color:var(--ts-text-soft-2);font-size:1.05rem}
    .ie-close{width:34px;height:34px;border:0;border-radius:50%;background:var(--ts-wash-06);color:var(--ts-text-sky-150-15);font-size:1.2rem;cursor:pointer}
    .ie-body{display:grid;grid-template-columns:minmax(0,1fr) minmax(260px,.85fr);gap:1.25rem;padding:1.25rem}
    .ie-preview{display:grid;place-items:center;padding:.5rem;border-radius:14px;background:var(--ts-bg-navy-900-8)}
    .ie-preview canvas{width:100%;max-width:320px;height:auto;border-radius:10px;box-shadow:0 12px 30px var(--ts-shadow-gray-1000-a35)}
    .ie-controls{display:grid;gap:.85rem;align-content:start}
    .ie-field{display:grid;gap:.35rem;font-size:.72rem;font-weight:700;color:var(--ts-text-sky-300-14)}
    .ie-field input[type=text],.ie-field textarea,.ie-field select{box-sizing:border-box;width:100%;padding:.55rem .65rem;border:1px solid var(--ts-border-navy-700-3);border-radius:9px;background:var(--ts-scrollbar-track);color:var(--ts-text-soft-2);font-size:.82rem;font-weight:500}
    .ie-field select{cursor:pointer}
    .ie-scope-hint{color:var(--ts-text-sky-350-23);font-weight:500}
    .ie-field input[type=color]{width:100%;height:38px;padding:2px;border:1px solid var(--ts-border-navy-700-3);border-radius:9px;background:var(--ts-scrollbar-track);cursor:pointer}
    .ie-field-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.6rem}
    .ie-toggle-row{display:flex;align-items:center;gap:.6rem}
    .ie-toggle-row small{color:var(--ts-text-sky-350-23);font-weight:500}
    .ie-switch{position:relative;display:inline-block;flex:0 0 auto;width:52px;height:28px;cursor:pointer}
    .ie-switch input{position:absolute;inset:0;width:100%;height:100%;margin:0;opacity:0;cursor:pointer}
    .ie-switch-track{position:absolute;inset:0;border-radius:999px;background:var(--ts-bg-navy-700-2);pointer-events:none;transition:background .15s ease}
    .ie-switch-knob{position:absolute;top:3px;left:3px;width:22px;height:22px;border-radius:50%;background:var(--ts-bg-sky-100-3);transition:transform .15s ease}
    .ie-switch input:checked ~ .ie-switch-track{background:var(--ts-bg-blue-500-3)}
    .ie-switch input:checked ~ .ie-switch-track .ie-switch-knob{transform:translateX(24px);background:var(--ts-bg-gray-0)}
    .ie-switch input:focus-visible ~ .ie-switch-track{box-shadow:0 0 0 3px var(--ts-shadow-blue-400-a40)}
    .ie-actions{display:flex;justify-content:flex-end;gap:.6rem;margin-top:.4rem}
    .ie-btn{min-height:40px;padding:.6rem 1rem;border-radius:9px;font-weight:750;font-size:.82rem;cursor:pointer;border:1px solid transparent}
    .ie-btn-ghost{background:transparent;border-color:var(--ts-border-navy-700-3);color:var(--ts-text-sky-150-15)}
    .ie-btn-primary{background:var(--ts-bg-blue-500-3);color:var(--ts-text-on-accent)}
    @media(max-width:680px){.ie-body{grid-template-columns:1fr}.ie-field-row{grid-template-columns:1fr}}
  `],
})
export class InvitationEditorComponent implements OnChanges {
  /** URL del NEGOCIO (opción "Todo el negocio" del selector). */
  @Input({ required: true }) url!: string;
  @Input({ required: true }) code!: string;
  @Input() businessName = '';
  @Input() initialPrimaryColor: string | null = null;
  @Input() initialSecondaryColor: string | null = null;
  /** Espacios del negocio disponibles para generar la MISMA plantilla pero
   * apuntando al enlace de un espacio puntual en vez del sitio general --
   * el código de acceso es siempre el del negocio (los espacios no tienen
   * uno propio, ver helpers/privateAccess.ts), solo cambia el destino del QR. */
  @Input() spaces: Array<{ id: number; title: string; url: string }> = [];
  /** Preselecciona un espacio al abrir (ej. desde el botón "Crear imagen
   * para imprimir" de una tarjeta puntual en Gestión de salas). */
  @Input() initialSpaceId: number | null = null;
  @Output() close = new EventEmitter<void>();

  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  readonly canvasWidth = CANVAS_WIDTH;
  readonly canvasHeight = CANVAS_HEIGHT;

  selectedScope: 'business' | number = 'business';
  title = 'Acceso privado';
  subtitle = 'Escanea el código QR o visita el enlace e ingresa el código de acceso para reservar.';
  backgroundColor = '#0d2340';
  accentColor = '#2867d4';
  textColor = '#f4f8ff';
  showCode = true;

  private qrImage: HTMLImageElement | null = null;
  private logoImage: HTMLImageElement | null = null;

  /** Bug real corregido: la marca de agua inferior era el texto plano
   * "TuSala" dibujado en el canvas -- se reemplaza por el logo real del
   * sistema (mismo archivo que usa el login/dashboard). */
  constructor() {
    const img = new Image();
    img.onload = () => { this.logoImage = img; this.redraw(); };
    img.src = 'TuSala_LogoSinFondo.png';
  }

  /** Enlace legible para imprimir (sin "https://" ni "www."). */
  private get displayUrl(): string {
    return this.effectiveUrl.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  }

  private get effectiveUrl(): string {
    if (this.selectedScope === 'business') return this.url;
    return this.spaces.find(space => space.id === this.selectedScope)?.url || this.url;
  }

  private titleForScope(): string {
    const selectedSpace = this.selectedScope !== 'business' ? this.spaces.find(space => space.id === this.selectedScope) : null;
    if (selectedSpace) return `Acceso privado a ${selectedSpace.title}`;
    return this.businessName ? `Acceso privado a ${this.businessName}` : 'Acceso privado';
  }

  ngOnChanges(): void {
    this.selectedScope = this.initialSpaceId != null && this.spaces.some(space => space.id === this.initialSpaceId) ? this.initialSpaceId : 'business';
    this.title = this.titleForScope();
    this.backgroundColor = this.initialPrimaryColor ? this.darken(this.initialPrimaryColor) : this.backgroundColor;
    this.accentColor = this.initialSecondaryColor || this.initialPrimaryColor || this.accentColor;
    this.loadQr();
  }

  onScopeChange(): void {
    this.title = this.titleForScope();
    this.loadQr();
  }

  private darken(hex: string): string {
    const value = hex.replace('#', '');
    if (value.length !== 6) return this.backgroundColor;
    const r = Math.round(parseInt(value.slice(0, 2), 16) * 0.35);
    const g = Math.round(parseInt(value.slice(2, 4), 16) * 0.35);
    const b = Math.round(parseInt(value.slice(4, 6), 16) * 0.35);
    return `#${[r, g, b].map(n => n.toString(16).padStart(2, '0')).join('')}`;
  }

  private async loadQr(): Promise<void> {
    try {
      const dataUrl = await QRCode.toDataURL(this.effectiveUrl, { width: 480, margin: 1 });
      const img = new Image();
      img.onload = () => { this.qrImage = img; this.redraw(); };
      img.src = dataUrl;
    } catch {
      this.qrImage = null;
      this.redraw();
    }
  }

  redraw(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) { setTimeout(() => this.redraw(), 50); return; }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = this.canvasWidth;
    const h = this.canvasHeight;

    ctx.fillStyle = this.backgroundColor;
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = this.accentColor;
    ctx.fillRect(0, 0, w, 14);

    ctx.fillStyle = this.textColor;
    ctx.textAlign = 'center';
    ctx.font = '700 54px "Segoe UI", sans-serif';
    this.wrapText(ctx, this.title, w / 2, 160, w - 140, 62);

    ctx.font = '400 30px "Segoe UI", sans-serif';
    ctx.globalAlpha = 0.88;
    this.wrapText(ctx, this.subtitle, w / 2, 300, w - 200, 40);
    ctx.globalAlpha = 1;

    const qrSize = 420;
    const qrX = (w - qrSize) / 2;
    const qrY = 410;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(qrX - 22, qrY - 22, qrSize + 44, qrSize + 44);
    if (this.qrImage) ctx.drawImage(this.qrImage, qrX, qrY, qrSize, qrSize);

    // El texto pide "visitar el enlace": se imprime debajo del QR para quien
    // no pueda escanearlo (antes no aparecía en ninguna parte de la imagen).
    ctx.fillStyle = this.textColor;
    ctx.font = '600 26px "Segoe UI", sans-serif';
    this.wrapText(ctx, this.displayUrl, w / 2, qrY + qrSize + 70, w - 120, 32);

    if (this.showCode && this.code) {
      const boxY = qrY + qrSize + 128;
      ctx.fillStyle = this.textColor;
      ctx.globalAlpha = 0.8;
      ctx.font = '700 22px "Segoe UI", sans-serif';
      ctx.fillText('CÓDIGO DE ACCESO', w / 2, boxY - 14);
      ctx.globalAlpha = 1;
      ctx.fillStyle = this.accentColor;
      ctx.fillRect(w / 2 - 200, boxY, 400, 100);
      ctx.fillStyle = '#ffffff';
      ctx.font = '800 58px "Segoe UI", sans-serif';
      ctx.fillText(this.code, w / 2, boxY + 72);
    }

    if (this.logoImage) {
      const logoHeight = 60;
      const logoWidth = logoHeight * (this.logoImage.width / this.logoImage.height);
      ctx.drawImage(this.logoImage, w / 2 - logoWidth / 2, h - 30 - logoHeight, logoWidth, logoHeight);
    }
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number): void {
    const words = text.split(' ');
    let line = '';
    let currentY = y;
    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && line) {
        ctx.fillText(line, x, currentY);
        line = word;
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }
    if (line) ctx.fillText(line, x, currentY);
  }

  download(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const selectedSpace = this.selectedScope !== 'business' ? this.spaces.find(space => space.id === this.selectedScope) : null;
    const scopeSlug = selectedSpace
      ? '-' + selectedSpace.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
      : '';
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `invitacion-acceso-privado${scopeSlug}.png`;
    link.click();
  }
}
